import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, functions } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import './Onboarding.css';

// TypeScript interfaces
interface UserProfile {
  displayName: string;
  photoURL: string;
  bio: string;
  interests: string[];
  goals: string;
  followers: string[];
  following: string[];
  createdAt: Date | null;
}

interface SuggestedUser {
  uid: string;
  displayName: string;
  photoURL: string;
  bio: string;
  interests?: string[];
}

const Onboarding: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile>({
    displayName: '',
    photoURL: '',
    bio: '',
    interests: [],
    goals: '',
    followers: [],
    following: [],
    createdAt: null
  });
  const [suggestedUsers, setSuggestedUsers] = useState<SuggestedUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Available interests for selection
  const availableInterests = [
    'running', 'cycling', 'yoga', 'meditation', 'weightlifting', 'swimming',
    'vegan cooking', 'meal prep', 'nutrition', 'mental health', 'sleep',
    'stress management', 'mindfulness', 'therapy', 'fitness', 'wellness',
    'healthy eating', 'workout routines', 'self-care', 'personal growth'
  ];

  useEffect(() => {
    if (currentUser) {
      checkExistingProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const checkExistingProfile = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.interests && data.interests.length > 0) {
          // User already has interests, skip onboarding
          navigate('/dashboard');
        } else {
          // User exists but no interests, start onboarding
          setUserProfile(prev => ({
            ...prev,
            displayName: data.displayName || currentUser.displayName || '',
            photoURL: data.photoURL || currentUser.photoURL || '',
            bio: data.bio || '',
            interests: data.interests || [],
            goals: data.goals || '',
            followers: data.followers || [],
            following: data.following || [],
            createdAt: data.createdAt || new Date()
          }));
        }
      } else {
        // New user, set default values
        setUserProfile(prev => ({
          ...prev,
          displayName: currentUser.displayName || '',
          photoURL: currentUser.photoURL || '',
          createdAt: new Date()
        }));
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    }
  };

  const handleInterestToggle = (interest: string) => {
    setUserProfile(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleInputChange = (field: keyof UserProfile, value: any) => {
    setUserProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = async () => {
    if (currentStep === 1 && (!userProfile.displayName.trim() || userProfile.interests.length < 3)) {
      alert('Please enter a display name and select at least 3 interests.');
      return;
    }

    if (currentStep === 2 && !userProfile.goals.trim()) {
      alert('Please enter a health goal.');
      return;
    }

    if (currentStep === 3) {
      // Save profile and selected users
      await saveProfile();
      return;
    }

    if (currentStep === 2) {
      // Get suggested users after interests are set
      await getSuggestedUsers();
    }

    setCurrentStep(prev => prev + 1);
  };

  const getSuggestedUsers = async () => {
    try {
      const getSuggestedFollows = httpsCallable(functions, 'get_suggested_follows');
      const result = await getSuggestedFollows({ userId: currentUser!.uid });
      const data = result.data as any;
      setSuggestedUsers(data.users || []);
    } catch (error) {
      console.error('Error getting suggested users:', error);
    }
  };

  const handleUserToggle = (user: SuggestedUser) => {
    setSelectedUsers(prev => 
      prev.find(u => u === user.uid)
        ? prev.filter(u => u !== user.uid)
        : [...prev, user.uid]
    );
  };

  const saveProfile = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      // Save user profile
      await setDoc(doc(db, 'users', currentUser.uid), {
        ...userProfile,
        interests: userProfile.interests,
        updatedAt: new Date()
      });

      // Follow selected users
      if (selectedUsers.length > 0) {
        const followUser = httpsCallable(functions, 'follow_user');
        for (const uid of selectedUsers) {
          try {
            await followUser({ targetUid: uid, action: 'follow' });
          } catch (error) {
            console.error(`Error following user ${uid}:`, error);
          }
        }
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="onboarding-step">
      <h2>Welcome to Melio!</h2>
      <p>Let's set up your profile to connect with like-minded health enthusiasts.</p>
      
      <div className="form-group">
        <label>Display Name</label>
        <input
          type="text"
          value={userProfile.displayName}
          onChange={(e) => handleInputChange('displayName', e.target.value)}
          placeholder="Enter your display name"
          maxLength={50}
        />
      </div>

      <div className="form-group">
        <label>Bio (optional)</label>
        <textarea
          value={userProfile.bio}
          onChange={(e) => handleInputChange('bio', e.target.value)}
          placeholder="Tell us about yourself..."
          maxLength={140}
          rows={3}
        />
      </div>

      <div className="form-group">
        <label>Select Your Interests (choose 3-8)</label>
        <div className="interests-grid">
          {availableInterests.map(interest => (
            <button
              key={interest}
              type="button"
              className={`interest-chip ${userProfile.interests.includes(interest) ? 'selected' : ''}`}
              onClick={() => handleInterestToggle(interest)}
            >
              {interest}
            </button>
          ))}
        </div>
        <p className="interests-count">
          Selected: {userProfile.interests.length}/8
        </p>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="onboarding-step">
      <h2>Set Your Health Goal</h2>
      <p>What's your primary health goal for the next 30 days?</p>
      
      <div className="form-group">
        <label>Your Goal</label>
        <textarea
          value={userProfile.goals}
          onChange={(e) => handleInputChange('goals', e.target.value)}
          placeholder="e.g., Run 3 times per week, meditate daily, improve sleep quality..."
          maxLength={200}
          rows={4}
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="onboarding-step">
      <h2>Connect with Others</h2>
      <p>Follow people who share your interests to see their health journey updates.</p>
      
      {loading ? (
        <div className="loading">Finding people for you...</div>
      ) : suggestedUsers.length > 0 ? (
        <div className="suggested-users">
          {suggestedUsers.map(user => (
            <div
              key={user.uid}
              className={`user-card ${selectedUsers.includes(user.uid) ? 'selected' : ''}`}
              onClick={() => handleUserToggle(user)}
            >
              <div className="user-avatar">
                <img src={user.photoURL || '/default-avatar.png'} alt={user.displayName} />
              </div>
              <div className="user-info">
                <h4>{user.displayName}</h4>
                <p className="user-bio">{user.bio || 'No bio yet'}</p>
                <div className="shared-interests">
                  <span>Shared interests: {user.interests?.join(', ')}</span>
                </div>
              </div>
              <div className="user-select">
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.uid)}
                  readOnly
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-suggestions">
          <p>No suggestions available right now. You can follow people later from the Discover tab!</p>
        </div>
      )}
      
      <p className="selection-count">
        Selected: {selectedUsers.length} people
      </p>
    </div>
  );

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${(currentStep / 3) * 100}%` }}
          ></div>
        </div>

        <div className="step-indicator">
          Step {currentStep} of 3
        </div>

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}

        <div className="onboarding-actions">
          {currentStep > 1 && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCurrentStep(prev => prev - 1)}
              disabled={loading}
            >
              Back
            </button>
          )}
          
          <button
            type="button"
            className="btn-primary"
            onClick={handleNext}
            disabled={loading}
          >
            {loading ? 'Loading...' : currentStep === 3 ? 'Complete Setup' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding; 